import { Button, Col, Input, Row, Typography } from "antd";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPost, {
  authTokenString,
  baseURL,
} from "../../../hooks/useAxiosPost";
import StaffIcon from "./../../../assets/pngs/StaffProfile.png";
// import DummyFront from "./../../../assets/pngs/idCards/DummyFront.png";
// import DummyBack from "./../../../assets/pngs/idCards/DummyBack.png";
import { EditOutlined } from "@ant-design/icons";
import { ButtonProps } from "../../../helpers/constants";
import useMultiNotification from "../../../hooks/useNotification";
import NetwalaButton from "../../Items/Button/Index";
import TailSpinLoader from "../../Items/Loaders/TailSpinLoader";
import NetwalaSelect from "../../Items/Select/Index";
import StaffProfileTabCard from "../../staff/singleStaff/StaffProfileTabCard";
import StaffTransactionsCard from "../../staff/singleStaff/StaffTransactionsCard";
import "./../../staff/cards/SingleStaffMiniCard.css";

const ViewProfileComponent = () => {
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [staff, setStaffProfile] = useState<StaffListType | null>(null);
  const [staffTransactions, setStaffTransactions] = useState<
    StaffTransaction[]
  >([]);
  const [selectedConnectionType, setSelectedConnectionType] =
    useState<string>("");

  const [rechargeType, setRechargeType] = useState<string>("");
  const [username, setUserName] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [debouncedUsername, setDebouncedUsername] = useState<string>("");
  const [debouncedFullName, setDebouncedFullName] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationProp>({
    totalPages: 0,
    currentPage: 1,
    previousPage: null,
    nextPage: null,
    totalCount: 0,
    pageSize: 50,
  });
  const [
    getStaffTransactionsApi,
    transactionsApiResponse,
    transactionApiError,
    transactionsApiLoading,
  ] = useAxiosPost<ResponseProp>("invoices/getMyTransactions");
  useEffect(() => {
    if (transactionsApiResponse !== null) {
      const data: any = transactionsApiResponse.responseData;
      const {
        currentPage,
        nextPage,
        pageSize,
        previousPage,
        totalCount,
        totalPages,
        transactions,
      } = data;
      setPagination({
        currentPage,
        totalPages,
        nextPage,
        previousPage,
        totalCount,
        pageSize,
      });

      setStaffTransactions(transactions || []);
    }
  }, [transactionsApiResponse]);
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>("auth/getMyProfile");
  useEffect(() => {
    if (apiResponse !== null) {
      const data: any = apiResponse.responseData;
      setStaffProfile(data);
    }
  }, [apiResponse]);
  // Debounce username
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUsername(username);
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  // Debounce fullName
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFullName(fullName);
    }, 500);

    return () => clearTimeout(timer);
  }, [fullName]);

  // Track previous filter values to detect filter changes (not page/pageSize changes)
  const prevFiltersRef = useRef({
    debouncedUsername,
    debouncedFullName,
    selectedConnectionType,
    rechargeType,
  });

  // Fetch transactions when filters change (with debounce for username and fullName)
  useEffect(() => {
    // Check if filters changed (excluding page and pageSize changes)
    const filtersChanged =
      prevFiltersRef.current.debouncedUsername !== debouncedUsername ||
      prevFiltersRef.current.debouncedFullName !== debouncedFullName ||
      prevFiltersRef.current.selectedConnectionType !==
        selectedConnectionType ||
      prevFiltersRef.current.rechargeType !== rechargeType;

    // If filters changed, reset to page 1
    if (filtersChanged) {
      setCurrentPage(1);
    }

    const filters: any = {
      page: filtersChanged ? 1 : currentPage,
      connectionType: selectedConnectionType || null,
      username: debouncedUsername || null,
      fullName: debouncedFullName || null,
      rechargeType: rechargeType || null,
      pageSize,
    };
    getStaffTransactionsApi(filters);

    // Update previous filter values
    prevFiltersRef.current = {
      debouncedUsername,
      debouncedFullName,
      selectedConnectionType,
      rechargeType,
    };
  }, [
    debouncedUsername,
    debouncedFullName,
    selectedConnectionType,
    rechargeType,
    pageSize,
    currentPage,
  ]);

  useEffect(() => {
    postApiData({});
    // Initial load - filters will be handled by the filter useEffect above
  }, []);
  const apiReload = (isReload: boolean) => {};
  const handlePageSwitch = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // useEffect will handle the API call
  };
  const handlePageSizeChange = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    // useEffect will handle the API call
  };
  const handleClickEdit = () => {
    // Simulate click on hidden file input element

    const imageInput = document.getElementById("imageInput");
    if (imageInput) {
      imageInput.click();
    }
  };
  const [imageFile, setImageFile] = useState<File | null>(null);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setImageFile(event.target.files?.[0] ?? null);
    if (event.target.files && event.target.files.length > 0) {
      setIsLoading(true);

      const formData = new FormData();
      formData.append("authTokenString", authTokenString.toString());
      formData.append("profilePicture", event.target.files[0]);

      try {
        const response = await fetch(`${baseURL}/auth/updateMyProfilePicture`, {
          method: "POST",
          body: formData,
          // Note: When using FormData, do NOT set the Content-Type header
          // The browser will set it automatically, including the boundary parameter
        });

        if (response.ok) {
          const responseData = await response.json();
          openNotification(
            "success",
            "Success",
            responseData?.message || "Profile Updated Successfully"
          );
          postApiData({});
          setIsLoading(false);
        } else {
          const responseData = await response.json();

          openNotification(
            "error",
            "Operation Failed",
            responseData?.message || "Server Error"
          );
          console.error("Upload failed:", responseData);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        console.error("Error during fetch:", error);
      }
    }
  };
  if (apiLoading || isLoading) {
    return (
      <Row style={{ width: "100%" }}>
        <Col span={24}>
          <TailSpinLoader />
        </Col>
      </Row>
    );
  }
  return (
    <>
      {contextHolder}
      <Row gutter={[20, 20]} style={{ marginTop: "40px" }}>
        {staff && (
          <>
            <Col span={12}>
              <div
                className="staff-mini-card-container"
                style={{ background: "inherit", border: "none" }}
              >
                <div className="staff-image-container">
                  <img
                    src={
                      staff.url && staff.isEncrypted === 0
                        ? staff.url
                        : StaffIcon
                    }
                    className="staff-profile-image"
                  />
                  <div className="edit-icon-staff-profile">
                    <Button
                      icon={<EditOutlined />}
                      onClick={handleClickEdit}
                    ></Button>
                  </div>
                  <input
                    type="file"
                    id="imageInput"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </div>

                <Typography className="staff-full-name">
                  {staff.firstName} {staff.lastName}
                </Typography>
                <Typography className="staff-phone-number">
                  {staff.phoneNumber}
                </Typography>
                <Typography className="staff-user-role">
                  {staff.headType}
                </Typography>
                {["RMAN", "CASHIER"].includes(staff.headType) && (
                  <Typography className="">
                    <span className="staff-cash-label">Cash:</span>{" "}
                    <span className="staff-cash-value">
                      {staff?.userBalance?.cashAvailable || 0}
                    </span>
                  </Typography>
                )}
                {/* <div className="id-card-view-container">
                  <img src={DummyFront} />
                  <img src={DummyBack} />
                </div> */}
              </div>
            </Col>

            <Col span={12} style={{ marginTop: "30px" }}>
              <StaffProfileTabCard
                isStaff={false}
                staff={staff}
                staffApiReload={apiReload}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >
                <NetwalaButton
                  backgroundColor={ButtonProps.background.STANDARD}
                  height={ButtonProps.height.STANDARD}
                  width={ButtonProps.width.STANDARD}
                  textColor={ButtonProps.color.STANDARD}
                  onClick={() => {
                    navigate(`/auth/update-profile`);
                  }}
                  text="Update Profile"
                />
              </div>
            </Col>
          </>
        )}
      </Row>

      {transactionsApiLoading === true ? (
        <TailSpinLoader />
      ) : (
        <Row gutter={[20, 20]} style={{ marginTop: "40px" }}>
          <Col span={24} style={{ marginTop: "30px" }}>
            {/* Filters Here */}

            <Row gutter={20}>
              <Col span={6}>
                <span className="create-user-form-label">Connection Type</span>
                <NetwalaSelect
                  value={selectedConnectionType}
                  onChange={(e) => {
                    setSelectedConnectionType(e);
                  }}
                  size="large"
                  height="45px"
                  options={[
                    { label: "All", value: "" },
                    { label: "INTERNET", value: "INTERNET" },
                    { label: "CATV", value: "CATV" },
                  ]}
                />
              </Col>
              <Col span={6}>
                <span className="create-user-form-label">Username</span>
                <Input
                  className="user-input-general-height"
                  placeholder="johndoe"
                  value={username}
                  onChange={(e) => setUserName(e.target.value)}
                />
              </Col>
              <Col span={6}>
                <span className="create-user-form-label">Full Name</span>
                <Input
                  className="user-input-general-height"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Col>
              {/* <Col span={6}>
                <span className="create-user-form-label">Recharge Type</span>
                <NetwalaSelect
                  value={rechargeType}
                  onChange={(e) => {
                    setRechargeType(e);
                  }}
                  size="large"
                  height="45px"
                  options={[
                    { label: "All", value: "" },
                    { label: "RECHARGE", value: "RECHARGE" },
                    { label: "NEW", value: "NEW" },
                  ]}
                />
              </Col> */}
            </Row>
          </Col>
          <Col span={24}>
            <StaffTransactionsCard
              transactions={staffTransactions}
              pagination={pagination}
              handlePageSwitch={handlePageSwitch}
              handlePageSizeChange={handlePageSizeChange}
              intPkHeadID={Number(staff?.intPkHeadID)}
              viewType="profile"
            />
          </Col>
        </Row>
      )}
    </>
  );
};
export default ViewProfileComponent;
